import * as React from "react";
import { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from "@material-ui/icons";
import axios from "axios";
import { useNotify } from "react-admin";

import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPost, getConfigPatch, getConfigDelete } from "../getConfig";

interface ConstantsKeyValue {
  id?: number;
  key: string;
  value: string;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

const styles = {
  container: {
    padding: "20px",
    maxWidth: "1200px",
    margin: "0 auto",
  },
  card: {
    marginBottom: "20px",
  },
  tableContainer: {
    marginTop: "20px",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  buttonGroup: {
    display: "flex",
    gap: "10px",
  },
  dialogContent: {
    padding: "20px",
    minWidth: "400px",
  },
  formField: {
    marginBottom: "20px",
  },
  actionCell: {
    display: "flex",
    gap: "5px",
  },
  valueCell: {
    maxWidth: "400px",
    wordBreak: "break-word" as const,
  },
};

const ConstantsKeyValueDashboard = () => {
  const notify = useNotify();
  const [constants, setConstants] = useState<ConstantsKeyValue[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedConstant, setSelectedConstant] = useState<ConstantsKeyValue | null>(null);
  const [formData, setFormData] = useState<{ key: string; value: string }>({
    key: "",
    value: "",
  });
  const [searchTerm, setSearchTerm] = useState("");

  const fetchConstants = useCallback(async () => {
    try {
      setLoading(true);
      const config = getConfigGet({});
      const response = await axios.get(ENDPOINTS.CONSTANTS_KEY_VALUE_DASHBOARD, config);
      setConstants(response.data as ConstantsKeyValue[]);
    } catch (err: any) {
      notify(err.response?.data?.message || "Failed to fetch constants", "error");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchConstants();
  }, [fetchConstants]);

  const handleCreate = async () => {
    if (!formData.key.trim()) {
      notify("Please enter a key", "error");
      return;
    }

    try {
      setActionLoading(true);
      const config = getConfigPost();
      await axios.post(
        ENDPOINTS.CONSTANTS_KEY_VALUE_DASHBOARD,
        {
          key: formData.key,
          value: formData.value,
        },
        { headers: config }
      );
      notify(`Constant "${formData.key}" created successfully`, "info");
      setFormData({ key: "", value: "" });
      setOpenCreateDialog(false);
      await fetchConstants();
    } catch (err: any) {
      notify(err.response?.data?.message || "Failed to create constant", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedConstant) return;

    try {
      setActionLoading(true);
      const config = getConfigPatch();
      await axios.patch(
        ENDPOINTS.CONSTANTS_KEY_VALUE_DASHBOARD,
        {
          key: selectedConstant.key,
          value: formData.value,
        },
        { headers: config }
      );
      notify(`Constant "${selectedConstant.key}" updated successfully`, "info");
      setSelectedConstant(null);
      setFormData({ key: "", value: "" });
      setOpenEditDialog(false);
      await fetchConstants();
    } catch (err: any) {
      notify(err.response?.data?.message || "Failed to update constant", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedConstant) return;

    try {
      setActionLoading(true);
      const config = getConfigDelete();
      await axios.delete(`${ENDPOINTS.CONSTANTS_KEY_VALUE_DASHBOARD}/${selectedConstant.key}`, {
        headers: config,
      });
      notify(`Constant "${selectedConstant.key}" deleted successfully`, "info");
      setSelectedConstant(null);
      setOpenDeleteDialog(false);
      await fetchConstants();
    } catch (err: any) {
      notify(err.response?.data?.message || "Failed to delete constant", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const openEditDialogHandler = (constant: ConstantsKeyValue) => {
    setSelectedConstant(constant);
    setFormData({ key: constant.key, value: constant.value });
    setOpenEditDialog(true);
  };

  const openDeleteDialogHandler = (constant: ConstantsKeyValue) => {
    setSelectedConstant(constant);
    setOpenDeleteDialog(true);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const filteredConstants = constants.filter(
    (c) =>
      c.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div style={styles.container}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.headerRow}>
        <Typography variant="h4">Constants Key-Value Dashboard</Typography>
        <div style={styles.buttonGroup}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchConstants}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
              setFormData({ key: "", value: "" });
              setOpenCreateDialog(true);
            }}
          >
            Add New Constant
          </Button>
        </div>
      </div>

      <Card style={styles.card}>
        <CardContent>
          <TextField
            fullWidth
            label="Search by key or value"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ marginBottom: "20px" }}
          />

          <Typography variant="subtitle1" gutterBottom>
            Total: {filteredConstants.length} constants
          </Typography>

          <TableContainer component={Paper} style={styles.tableContainer}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Key</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Updated By</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Updated At</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredConstants.map((constant) => (
                  <TableRow key={constant.id || constant.key}>
                    <TableCell>{constant.id || "N/A"}</TableCell>
                    <TableCell>
                      <strong>{constant.key}</strong>
                    </TableCell>
                    <TableCell style={styles.valueCell}>{constant.value}</TableCell>
                    <TableCell>{constant.createdBy || "N/A"}</TableCell>
                    <TableCell>{constant.updatedBy || "N/A"}</TableCell>
                    <TableCell>{formatDate(constant.createdAt)}</TableCell>
                    <TableCell>{formatDate(constant.updatedAt)}</TableCell>
                    <TableCell>
                      <div style={styles.actionCell}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openEditDialogHandler(constant)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => openDeleteDialogHandler(constant)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredConstants.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No constants found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Constant</DialogTitle>
        <DialogContent style={styles.dialogContent}>
          <TextField
            fullWidth
            label="Key"
            value={formData.key}
            onChange={(e) => setFormData({ ...formData, key: e.target.value })}
            style={styles.formField}
            placeholder="Enter constant key"
            variant="outlined"
          />
          <TextField
            fullWidth
            label="Value"
            value={formData.value}
            onChange={(e) => setFormData({ ...formData, value: e.target.value })}
            style={styles.formField}
            placeholder="Enter constant value"
            variant="outlined"
            multiline
            rows={4}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            color="primary"
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Constant</DialogTitle>
        <DialogContent style={styles.dialogContent}>
          <TextField
            fullWidth
            label="Key"
            value={selectedConstant?.key || ""}
            style={styles.formField}
            variant="outlined"
            disabled
          />
          <TextField
            fullWidth
            label="Value"
            value={formData.value}
            onChange={(e) => setFormData({ ...formData, value: e.target.value })}
            style={styles.formField}
            placeholder="Enter new value"
            variant="outlined"
            multiline
            rows={4}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleEdit}
            color="primary"
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the constant{" "}
            <strong>"{selectedConstant?.key}"</strong>?
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: "10px" }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="secondary"
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ConstantsKeyValueDashboard;
